/**
 * IT-ASSET-REPORT-002 — Consolidated Product Inventory (Reports tab).
 * One row per Product Type/Name: Total · Working · Non-Working · Other/Unspecified, Grand Total.
 * Source: GET /api/it-reports/assets/products (Stock State is authoritative). Excel export honours filters.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Boxes, Download, Loader, RotateCcw, Search, Monitor, Users, Package, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const Num = ({ v, tone, testId }) => <td className={`pir-num ${tone || ''}`} data-testid={testId}>{v}</td>;

const ProductInventoryReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ asset_type_id: '', stock_state: '', location: '' });
  const [locDraft, setLocDraft] = useState('');

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [f]);

  useEffect(() => {
    fetch(`${API_URL}/api/it-asset-types`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : { types: [] })).then((d) => setTypes(d.types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let live = true; setLoading(true);
    fetch(`${API_URL}/api/it-reports/assets/products?${params()}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
      .then((d) => { if (live) setData(d); })
      .catch((e) => toast.error(e.message)).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [params]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const p = params(); p.set('format', 'excel');
      const res = await fetch(`${API_URL}/api/it-reports/assets/products?${p}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const name = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || 'asset_product_inventory.xlsx';
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Consolidated report ready — ${res.headers.get('x-total-records') || ''} assets`);
    } catch (e) { toast.error(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const reset = () => { setF({ asset_type_id: '', stock_state: '', location: '' }); setLocDraft(''); };
  const g = data?.grand_total;
  const rows = data?.rows || [];

  return (
    <section className="pir" data-testid="product-inventory-report">
      <header className="pir-head">
        <div className="pir-title"><span className="pir-icon"><Boxes size={18} /></span>
          <div><h4>Consolidated Product Inventory</h4><p>Quantities grouped by Product Type · Stock State is the source of truth</p></div></div>
        <button type="button" className="pir-export" onClick={exportXlsx} disabled={exporting || loading} data-testid="pir-export-btn">
          {exporting ? <Loader size={14} className="rpt-spin" /> : <Download size={14} />} Export Excel
        </button>
      </header>
      <div className="pir-filters" data-testid="pir-filters">
        <select value={f.asset_type_id} onChange={(e) => setF({ ...f, asset_type_id: e.target.value })} data-testid="pir-filter-type">
          <option value="">All product types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={f.stock_state} onChange={(e) => setF({ ...f, stock_state: e.target.value })} data-testid="pir-filter-stock">
          <option value="">All stock states</option><option value="working">Working</option><option value="non_working">Non-Working</option><option value="unspecified">Other / Unspecified</option>
        </select>
        <form className="pir-search" onSubmit={(e) => { e.preventDefault(); setF({ ...f, location: locDraft.trim() }); }}>
          <Search size={13} /><input value={locDraft} onChange={(e) => setLocDraft(e.target.value)} placeholder="Location contains…" data-testid="pir-filter-location" />
        </form>
        <button type="button" className="pir-reset" onClick={reset} data-testid="pir-reset"><RotateCcw size={13} /> Reset</button>
      </div>
      {g && (
        <div className="pir-summary pir-summary-5" data-testid="pir-summary">
          <div><i className="pir-kpi-ico"><Monitor size={18} /></i><div><span>Total Assets</span><strong data-testid="pir-total">{g.total}</strong></div></div>
          <div className="ok"><i className="pir-kpi-ico"><Users size={18} /></i><div><span>Assigned (Working)</span><strong data-testid="pir-assigned">{g.assigned}</strong></div></div>
          <div className="ok2"><i className="pir-kpi-ico"><Package size={18} /></i><div><span>Working Stock</span><strong data-testid="pir-working-stock">{g.working_stock}</strong></div></div>
          <div className="bad"><i className="pir-kpi-ico"><AlertTriangle size={18} /></i><div><span>Non-Working</span><strong data-testid="pir-non-working">{g.non_working}</strong></div></div>
          <div className="muted"><i className="pir-kpi-ico"><Info size={18} /></i><div><span>Other / Unspecified</span><strong data-testid="pir-other">{g.other}</strong></div></div>
        </div>
      )}
      <div className="pir-table-wrap">
        {loading ? <div className="rpt-loading"><Loader size={18} className="rpt-spin" /><p>Calculating quantities…</p></div> : (
          <table className="pir-table" data-testid="pir-table">
            <thead>
              <tr>
                <th rowSpan={2}>Product Name</th><th rowSpan={2} className="pir-num">Total Assets</th>
                <th colSpan={2} className="pir-group-head" data-testid="pir-working-group">Working Quantity</th>
                <th rowSpan={2} className="pir-num">Non-Working Quantity</th><th rowSpan={2} className="pir-num">Other / Unspecified</th>
              </tr>
              <tr><th className="pir-num pir-sub-head">Assigned Quantity</th><th className="pir-num pir-sub-head">Working Stock Quantity</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="pir-empty" data-testid="pir-empty">No assets match these filters</td></tr>}
              {rows.map((r) => (
                <tr key={r.product_name} data-testid={`pir-row-${r.product_name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <td className="pir-name">{r.product_name}</td>
                  <Num v={r.total} /><Num v={r.assigned} tone="ok pir-grp" /><Num v={r.working_stock} tone="ok2 pir-grp" /><Num v={r.non_working} tone="bad" /><Num v={r.other} tone="muted" />
                </tr>
              ))}
            </tbody>
            {g && rows.length > 0 && (
              <tfoot><tr data-testid="pir-grand-total"><td>Grand Total</td><Num v={g.total} /><Num v={g.assigned} tone="ok pir-grp" testId="pir-gt-assigned" /><Num v={g.working_stock} tone="ok2 pir-grp" testId="pir-gt-working-stock" /><Num v={g.non_working} tone="bad" /><Num v={g.other} tone="muted" /></tr></tfoot>
            )}
          </table>
        )}
      </div>
    </section>
  );
};

export default ProductInventoryReport;
